import { ApiClient } from '../../client';
import { ApiClientOptions } from '../../types';
import {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  ProductsQueryParams,
  ProductResponse,
  PutProductResult,
  ForkProductRequest,
  ForkProductResponse,
  ForkAllProductRequest,
  ForkAllProductResponse,
  ProductUploadRequest,
  ProductUploadResponse,
  CommitChangesRequest,
  FlatProductQueryResponse,
  CasReviewCandidate,
  CasCandidateActionResponse,
  NamePrecedenceStep,
  ProductNameVariant,
  ExternalLinkSource,
  ExternalLinkConsequences,
  PreviewBreakLinkResponse,
  BreakLinkResponse,
  ExternalLinkSearchResponse,
  ExternalLinkSuggestionsResponse,
  CreateExternalLinksRequest,
  CreateExternalLinksResponse,
  ProductIdsResponse,
  CasQuarantineQueueListParams,
  CasQuarantineQueueListResponse,
  CasQuarantineReviewDetail,
  CasQuarantineReSearchRequest,
  CasQuarantineReSearchResponse,
  CasQuarantineSplitRequest,
  CasQuarantineSplitResponse,
  FdaSourceKey,
  FdaSourceSearchResponse,
} from './types';
import { convertDatesToStrings } from '../../../../utils/dateConversion';

const SEARCH_PERF_LOG_TAG = '[PELS-CLIENT-PERF]';
const SHOULD_LOG_SEARCH_PERF =
  process.env.REACT_APP_ENV === 'stage' ||
  process.env.REACT_APP_ENV === 'development';
let searchPerfRequestCounter = 0;

function serializeSearchPerfPayload(payload: Record<string, unknown>): string {
  try {
    return JSON.stringify(payload);
  } catch {
    return JSON.stringify({ error: 'Failed to serialize search perf payload' });
  }
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      String((error as { name?: string }).name) === 'AbortError') ||
    (typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      String((error as { code?: string }).code) === 'ERR_CANCELED')
  );
}

export class ProductService {
  constructor(private api: ApiClient) {}

  async getById(id: number) {
    return this.api.get<Product>(`/products/${id}`);
  }

  async getNamePrecedenceConfig() {
    return this.api.get<NamePrecedenceStep[]>(
      '/products/name-precedence-config'
    );
  }

  async getNameVariants(id: number) {
    return this.api.get<ProductNameVariant[]>(`/products/${id}/name-variants`);
  }

  async create(data: CreateProductRequest) {
    const payload = convertDatesToStrings(data);
    return this.api.post<ProductResponse>('/products', payload);
  }

  async update(data: UpdateProductRequest) {
    const payload = convertDatesToStrings(data);
    return this.api.put<PutProductResult>(
      `/products/${data.product_id}`,
      payload
    );
  }

  async delete(id: number) {
    return this.api.delete(`/products/${id}`);
  }

  async fork(productId: number, data: ForkProductRequest) {
    return this.api.post<ForkProductResponse>(
      `/products/${productId}/fork`,
      data
    );
  }

  async forkAll(productId: number, data: ForkAllProductRequest) {
    return this.api.post<ForkAllProductResponse>(
      `/products/${productId}/fork-all`,
      data
    );
  }

  // Alternative CAS source candidates — confirm/reject (issue #350, FR-7)
  async confirmCasCandidate(
    productId: number,
    reviewId: number,
    candidate: CasReviewCandidate
  ) {
    return this.api.post<CasCandidateActionResponse>(
      `/products/${productId}/cas-candidates/${reviewId}/confirm`,
      { candidate }
    );
  }

  async rejectCasCandidate(productId: number, reviewId: number) {
    return this.api.post<CasCandidateActionResponse>(
      `/products/${productId}/cas-candidates/${reviewId}/reject`,
      {}
    );
  }

  // CAS Quarantine Resolution admin queue (issue #662, CQR-012 → CQR-015).
  async getQuarantineQueue(params?: CasQuarantineQueueListParams) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.reasons) {
      params.reasons.forEach(reason => queryParams.append('reason', reason));
    }
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection)
      queryParams.append('sortDirection', params.sortDirection);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize)
      queryParams.append('pageSize', params.pageSize.toString());

    const query = queryParams.toString();
    const url = `/products/cas-review-queue${query ? `?${query}` : ''}`;
    return this.api.get<CasQuarantineQueueListResponse>(url);
  }

  async getQuarantineReviewDetail(productId: number) {
    return this.api.get<CasQuarantineReviewDetail>(
      `/products/${productId}/cas-review-queue`
    );
  }

  async reSearchCasCandidates(
    productId: number,
    data: CasQuarantineReSearchRequest
  ) {
    return this.api.post<CasQuarantineReSearchResponse>(
      `/products/${productId}/cas-candidates/re-search`,
      data
    );
  }

  async splitQuarantineReviewRow(
    productId: number,
    reviewId: number,
    data: CasQuarantineSplitRequest
  ) {
    return this.api.post<CasQuarantineSplitResponse>(
      `/products/${productId}/cas-review-queue/${reviewId}/split`,
      data
    );
  }

  // External Source Linking — break-link preview/commit (issue #622, Task
  // 6.3/6.4 API, Task 8.3 UI). Routed at /api/external-links, not
  // /api/products, per Architecture Decision 17 (one route family rooted on
  // the product, source as a path param) — see docs/API.md's "External
  // Source Linking" section.
  async previewBreakExternalLink(
    productId: number,
    source: ExternalLinkSource,
    recordId: number
  ) {
    return this.api.get<PreviewBreakLinkResponse>(
      `/external-links/${productId}/${source}/${recordId}/preview`
    );
  }

  /**
   * @param expectedConsequences - the exact `comparable` object a prior
   *   `previewBreakExternalLink` call returned, echoed back so the server can
   *   recompute-and-abort on drift (Architecture Decision 19). Omitting it
   *   (null) skips that check and the break proceeds unconditionally — used
   *   when the preview itself failed to load (Sub-task 8.3 AC3).
   */
  async breakExternalLink(
    productId: number,
    source: ExternalLinkSource,
    recordId: number,
    expectedConsequences: ExternalLinkConsequences | null
  ) {
    return this.api.delete<BreakLinkResponse>(
      `/external-links/${productId}/${source}/${recordId}`,
      expectedConsequences
        ? { data: { expected_consequences: expectedConsequences } }
        : undefined
    );
  }

  // External Source Linking — search (issue #622, Task 7.1 API, Task 9.2
  // UI). Mounted at /api/external-link-search, its OWN router/base path
  // (see api/routes/externalLinkSearch.js's header) — NOT nested under
  // /api/external-links like the create/break/preview family above, since
  // this route is source-rooted only, not product-rooted.
  async searchExternalLinkSource(
    source: ExternalLinkSource,
    query: string,
    page = 1,
    pageSize = 20,
    productId?: number,
    signal?: AbortSignal
  ) {
    const params = new URLSearchParams({
      q: query,
      page: String(page),
      pageSize: String(pageSize),
    });
    // Optional (search stays source-rooted, not product-rooted) — when
    // given, each result carries `is_linked` relative to this product
    // (issue #681, item 2).
    if (productId !== undefined) {
      params.set('productId', String(productId));
    }
    return this.api.get<ExternalLinkSearchResponse>(
      `/external-link-search/${source}?${params.toString()}`,
      signal ? { signal } : undefined
    );
  }

  // External Source Linking — suggestions (issue #622, Task 7.2 API, Task
  // 9.3 UI). Index-addressed, one call per variant (see
  // api/routes/externalLinkSearch.js's header for why this is a fallback,
  // not real server-push streaming) — the caller drives the loop by
  // reading `total_variants` off variantIndex 0's response and firing
  // variantIndex = 1..total_variants-1 itself; this method only wraps ONE
  // call, deliberately, so ExternalLinkSuggestionsPane controls sequencing/
  // concurrency and can update state as each call resolves.
  async getExternalLinkSuggestions(
    productId: number,
    source: ExternalLinkSource,
    variantIndex: number
  ) {
    return this.api.get<ExternalLinkSuggestionsResponse>(
      `/external-link-search/${productId}/suggestions/${source}?variantIndex=${variantIndex}`
    );
  }

  // FDA Source Product Search (docs/features/fda-source-product-search.md).
  // Mounted at /api/products/fda-source-search, its OWN router (see
  // api/routes/fdaSourceSearch.js's header) — three independent per-source
  // searches (Architecture Decision 10), never a UNION.
  //
  // Always called with a SPECIFIC source (never 'all') -- one call per
  // visible column, each with its own page number -- so each of
  // FdaSourceSearchResults.tsx's three columns gets genuinely independent
  // loading and pagination (Decision 12), rather than all three arriving
  // together on one combined response. The endpoint's `source=all` +
  // per-source `obPage`/`ndcPage`/`dmfPage` combined-response shape still
  // exists and is tested (FSS-005) for any future caller that wants one
  // round trip; this client method's simpler (source, page) shape is just
  // this frontend's own chosen usage of that same endpoint.
  async searchFdaSources(
    search: string,
    source: FdaSourceKey,
    page = 1,
    pageSize = 25,
    signal?: AbortSignal,
    linkFilter?: 'all' | 'unlinked'
  ) {
    // The route always reads the source-prefixed page param
    // (obPage/ndcPage/dmfPage) regardless of which `source` value is sent
    // (api/routes/fdaSourceSearch.js's parsePaginationForSource) -- there is
    // no generic `page` param.
    const params = new URLSearchParams({
      search,
      source,
      pageSize: String(pageSize),
      [`${source}Page`]: String(page),
    });
    // 'all' is the API's own default -- omitted rather than sent explicitly,
    // so an unlinked-mode-unaware caller's request is byte-identical to
    // before this param existed (docs/features/fda-source-record-browser.md,
    // Task FSU-004).
    if (linkFilter === 'unlinked') {
      params.set('linkFilter', linkFilter);
    }
    return this.api.get<FdaSourceSearchResponse>(
      `/products/fda-source-search?${params.toString()}`,
      signal ? { signal } : undefined
    );
  }

  // External Source Linking — create (issue #622, Task 6.2 API, Task 9.5
  // UI). All-or-nothing batch create — see docs/API.md's "External Source
  // Linking" section for the pre-validation and already_linked semantics.
  // Called from LinkConfirmDialog.tsx (Sub-task 9.5).
  async createExternalLinks(
    productId: number,
    records: CreateExternalLinksRequest['records']
  ) {
    return this.api.post<CreateExternalLinksResponse>(
      `/external-links/${productId}`,
      { records }
    );
  }

  async deleteAll() {
    return this.api.delete('/products');
  }

  // File upload endpoints
  async uploadProducts(data: ProductUploadRequest) {
    const formData = new FormData();
    formData.append('file', data.file);
    if (Array.isArray(data.manufacturer_ids)) {
      data.manufacturer_ids.forEach(id => {
        formData.append('manufacturer_id', id.toString());
      });
    }

    return this.api.post<ProductUploadResponse>('/uploadProducts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async commitChanges(data: CommitChangesRequest) {
    return this.api.post('/commitProducts', {
      manufacturer_ids: Array.isArray(data.manufacturer_ids)
        ? data.manufacturer_ids
        : [],
      differences: JSON.stringify(data.differences),
    });
  }

  // Product-IDs-only endpoint (issue #637, VPE-010, Endpoint 7) — lighter
  // payload sibling to getAllFlat below, for callers that only need the ID
  // list matching the current filter (the validate-products devtool modal).
  async getIds(
    params?: Pick<
      ProductsQueryParams,
      'search' | 'advancedSearch' | 'limit' | 'offset'
    >,
    requestConfig?: ApiClientOptions
  ) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.advancedSearch)
      queryParams.append(
        'advancedSearch',
        JSON.stringify(params.advancedSearch)
      );
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = `/products/ids${query ? `?${query}` : ''}`;
    return this.api.get<ProductIdsResponse>(url, requestConfig);
  }

  // Flat products endpoint — used by ProductsRowGroupingDataGrid, the app's
  // only products grid. Returns denormalized Cartesian product for MUI
  // DataGrid row grouping.
  async getAllFlat(
    params?: ProductsQueryParams,
    requestConfig?: ApiClientOptions
  ) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.advancedSearch)
      queryParams.append(
        'advancedSearch',
        JSON.stringify(params.advancedSearch)
      );
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection)
      queryParams.append('sortDirection', params.sortDirection);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const isBasicSearchRequest =
      Boolean(params?.search && params.search.trim().length > 0) &&
      !params?.advancedSearch;

    if (isBasicSearchRequest) {
      queryParams.append('includeCartesianTotal', 'false');
    }

    const query = queryParams.toString();
    const url = `/products/flat${query ? `?${query}` : ''}`;

    const requestId = `flat-${++searchPerfRequestCounter}`;
    const startedAt =
      SHOULD_LOG_SEARCH_PERF && isBasicSearchRequest
        ? performance.now()
        : undefined;

    const effectiveRequestConfig: ApiClientOptions = isBasicSearchRequest
      ? {
          ...requestConfig,
          retries: 0,
        }
      : (requestConfig ?? {});

    if (startedAt !== undefined) {
      const payload = serializeSearchPerfPayload({
        requestId,
        searchTerm: params?.search,
        termLength: params?.search?.length ?? 0,
        limit: params?.limit,
        offset: params?.offset,
        sortBy: params?.sortBy,
        sortDirection: params?.sortDirection,
        startedAtIso: new Date().toISOString(),
      });
      console.info(
        `${SEARCH_PERF_LOG_TAG}[SearchPerf][FlatProducts][RequestStart] ${payload}`
      );
    }

    try {
      const response = await this.api.get<FlatProductQueryResponse>(
        url,
        effectiveRequestConfig
      );

      if (startedAt !== undefined) {
        const elapsedMs = performance.now() - startedAt;
        const payload = serializeSearchPerfPayload({
          requestId,
          searchTerm: params?.search,
          elapsedMs: Number(elapsedMs.toFixed(1)),
          resultRows: response.data?.products?.length ?? 0,
          totalProducts: response.data?.totalProducts ?? 0,
          totalRows: response.data?.total ?? 0,
          completedAtIso: new Date().toISOString(),
        });
        console.info(
          `${SEARCH_PERF_LOG_TAG}[SearchPerf][FlatProducts][RequestEnd] ${payload}`
        );
      }

      return response;
    } catch (error) {
      if (startedAt !== undefined) {
        const elapsedMs = performance.now() - startedAt;

        if (isAbortError(error)) {
          const abortPayload = serializeSearchPerfPayload({
            requestId,
            searchTerm: params?.search,
            elapsedMs: Number(elapsedMs.toFixed(1)),
            completedAtIso: new Date().toISOString(),
          });
          console.info(
            `${SEARCH_PERF_LOG_TAG}[SearchPerf][FlatProducts][RequestAbort] ${abortPayload}`
          );
          throw error;
        }

        const payload = serializeSearchPerfPayload({
          requestId,
          searchTerm: params?.search,
          elapsedMs: Number(elapsedMs.toFixed(1)),
          error: error instanceof Error ? error.message : String(error),
          completedAtIso: new Date().toISOString(),
        });
        console.warn(
          `${SEARCH_PERF_LOG_TAG}[SearchPerf][FlatProducts][RequestError] ${payload}`
        );
      }
      throw error;
    }
  }
}
